import { ReactNode } from "react";
import { Translate } from "components/common/Translate/Translate";
import { RouteNames } from "@/RouteNames";
import { getClipSnapSiteOrigin, getClipSnapToolLink } from "client/config/externalEditors";
import { getUploaderPathKey } from "@/store/features/bulkResize/utils/getSupportedExtensions";

export interface NavLinkProps {
    title: string | ReactNode;
    link: string;
    isEditor?: boolean;
    outlink?: boolean;
    isChild?: boolean;
    subItems?: NavChild[] | NavColumn[];
    urlMatcher?: (url: string, asPath: string, locale: string) => boolean;
}

export interface NavChild {
    title: string | ReactNode;
    link: string;
    outlink?: boolean;
    urlMatcher?: (url: string, asPath: string, locale: string) => boolean;
}

/** A dropdown column with an optional header, e.g. "Utility Tools" / "AI Tools" in the More menu. */
export interface NavColumn {
    header?: ReactNode;
    items: NavChild[];
}

export const isNavColumns = (subItems: NavChild[] | NavColumn[]): subItems is NavColumn[] =>
    subItems.length > 0 && "items" in subItems[0];

/** All leaf links of a dropdown, regardless of whether it is flat or split into columns. */
export const getNavChildren = (subItems: NavChild[] | NavColumn[]): NavChild[] =>
    isNavColumns(subItems) ? subItems.flatMap((column) => column.items) : subItems;

export const navLinks: NavLinkProps[] = [
    {
        title: <Translate keyName={"links:resize"} />,
        link: "/",
        subItems: [
            {
                title: <Translate keyName="image-resizer" />,
                link: "/",
                urlMatcher: (_url, asPath) => {
                    return getUploaderPathKey(asPath) === "resizer";
                },
            },
            {
                title: <Translate keyName={"bulk-resize"} />,
                link: "/bulk-resize",
                urlMatcher: (url) => url === "/bulk-resize/editor",
            },
            {
                title: <Translate keyName="links:png-resizer" />,
                link: RouteNames.pngResizer,
                urlMatcher: (_url, asPath) => {
                    return getUploaderPathKey(asPath) === "resize-png";
                },
            },
            {
                title: <Translate keyName="links:jpg-resizer" />,
                link: RouteNames.jpgResizer,
                urlMatcher: (_url, asPath) => {
                    return getUploaderPathKey(asPath) === "resize-jpg";
                },
            },
            {
                title: <Translate keyName="links:webp-resizer" />,
                link: RouteNames.webpResizer,
                urlMatcher: (url) => url === "/resize-webp/editor",
            },
        ],
    },
    {
        title: <Translate keyName={"links:croppers"} />,
        link: "/",
        subItems: [
            {
                title: <Translate keyName="image-cropper" />,
                link: RouteNames.cropImage,
                urlMatcher: (_url, asPath) => getUploaderPathKey(asPath) === "crop-image",
            },
            {
                title: <Translate keyName="png-cropper" />,
                link: RouteNames.pngCropper,
                urlMatcher: (_url, asPath) => getUploaderPathKey(asPath) === "crop-png",
            },
            {
                title: <Translate keyName="webp-cropper" />,
                link: RouteNames.webpCropper,
                urlMatcher: (_url, asPath) => getUploaderPathKey(asPath) === "crop-webp",
            },
            {
                title: <Translate keyName="jpg-cropper" />,
                link: RouteNames.jpgCropper,
                urlMatcher: (_url, asPath) => getUploaderPathKey(asPath) === "crop-jpg",
            },
        ],
    },
    {
        title: <Translate keyName={"links:image-compressors"} />,
        link: "/",
        subItems: [
            {
                title: <Translate keyName="image-compressor" />,
                link: RouteNames.imageCompressor,
                urlMatcher: (url) => url.includes("image-compressor"),
            },
            {
                title: <Translate keyName="jpeg-compressor" />,
                link: RouteNames.jpegCompressor,
                urlMatcher: (url) => url.includes("compress-jpeg"),
            },
            {
                title: <Translate keyName="png-compressor" />,
                link: RouteNames.pngCompressor,
                urlMatcher: (url) => url.includes("png-compressor"),
            },
            {
                title: <Translate keyName="gif-compressor" />,
                link: RouteNames.gifCompressor,
                urlMatcher: (url) => url.includes("gif-compressor"),
            },
        ],
        urlMatcher: (url) => url.includes("compressor"),
    },
    {
        title: <Translate keyName={"image-converters"} />,
        link: "/",
        urlMatcher: (_, asPath) => asPath.includes("-converter") || asPath.includes("-to-"),
        subItems: [
            {
                items: [
                    {
                        title: <Translate keyName={"image-converter"} />,
                        link: "/image-converter",
                        urlMatcher: (url) => url === "/image-converter/editor",
                    },
                    {
                        title: <Translate keyName={"format-converter"} variables={{ format: "SVG" }} />,
                        link: "/svg-converter",
                        urlMatcher: (_, asPath) => asPath === "/svg-converter",
                    },
                    {
                        title: <Translate keyName={"format-converter"} variables={{ format: "PNG" }} />,
                        link: "/png-converter",
                        urlMatcher: (_, asPath) => asPath === "/png-converter",
                    },
                    {
                        title: <Translate keyName={"format-converter"} variables={{ format: "JPG" }} />,
                        link: "/jpg-converter",
                        urlMatcher: (_, asPath) => asPath === "/jpg-converter",
                    },
                    {
                        title: <Translate keyName={"format-converter"} variables={{ format: "GIF" }} />,
                        link: "/gif-converter",
                        urlMatcher: (_, asPath) => asPath === "/gif-converter",
                    },
                ],
            },
            {
                items: [
                    {
                        title: <Translate keyName={"src-to-target"} variables={{ src: "HEIC", target: "JPG" }} />,
                        link: "/heic-to-jpg",
                        urlMatcher: (_, asPath) => asPath === "/heic-to-jpg",
                    },

                    {
                        title: <Translate keyName={"src-to-target"} variables={{ src: "WebP", target: "PNG" }} />,
                        link: "/webp-to-png",
                        urlMatcher: (_, asPath) => asPath === "/webp-to-png",
                    },
                    {
                        title: <Translate keyName={"src-to-target"} variables={{ src: "WebP", target: "JPG" }} />,
                        link: "/webp-to-jpg",
                        urlMatcher: (_, asPath) => asPath === "/webp-to-jpg",
                    },
                    {
                        title: <Translate keyName={"src-to-target"} variables={{ src: "PNG", target: "JPG" }} />,
                        link: "/png-to-jpg",
                        urlMatcher: (_, asPath) => asPath === "/png-to-jpg",
                    },
                    {
                        title: <Translate keyName={"src-to-target"} variables={{ src: "PNG", target: "SVG" }} />,
                        link: "/png-to-svg",
                        urlMatcher: (_, asPath) => asPath === "/png-to-svg",
                    },
                ],
            },
        ],
    },
    {
        title: (
            <span className="capitalize">
                <Translate keyName={"more"} />
            </span>
        ),
        link: "/",
        subItems: [
            {
                header: <Translate keyName="links:utility-tools" />,
                items: [
                    {
                        title: <Translate keyName="meme-generator" />,
                        link: "/meme-generator",
                    },
                    {
                        title: <Translate keyName="color-picker" />,
                        link: "/color-picker",
                    },
                    {
                        title: <Translate keyName="rotate-image" />,
                        link: "/rotate-image",
                    },
                    {
                        title: <Translate keyName="flip-image" />,
                        link: "/flip-image",
                    },
                    {
                        title: <Translate keyName="image-enlarger" />,
                        link: "/image-enlarger",
                        urlMatcher: (url) => url === "/image-enlarger/editor",
                    },
                ],
            },
            {
                header: <Translate keyName="links:ai-tools" />,
                items: [
                    {
                        title: <Translate keyName="links:remove-background" />,
                        link: getClipSnapToolLink("background-remover"),
                        outlink: true,
                    },
                    {
                        title: <Translate keyName="links:remove-objects" />,
                        link: getClipSnapToolLink("magic-eraser"),
                        outlink: true,
                    },
                    {
                        title: <Translate keyName="links:generative-fill" />,
                        link: getClipSnapToolLink("generative-fill"),
                        outlink: true,
                    },
                    {
                        title: <Translate keyName="links:ai-upscaler" />,
                        link: getClipSnapToolLink("image-upscaler"),
                        outlink: true,
                    },
                    {
                        title: <Translate keyName="links:ai-photo-editor" />,
                        link: getClipSnapSiteOrigin() + "/create",
                        outlink: true,
                    },
                ],
            },
        ],
    },
    {
        title: <Translate keyName={"links:pricing"} />,
        link: "/pricing",
        urlMatcher: (url) => {
            return url === "/pricing";
        },
    },
];
